import React from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { Draggable } from 'react-beautiful-dnd';

const getPromptId = (prompt) => prompt?._id || prompt?.id || '';

function PromptList({
  prompts,
  activePromptId,
  editingPromptId,
  editingText,
  onDelete,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  onSelect,
}) {
  return prompts.map((prompt, index) => {
    const promptId = getPromptId(prompt);
    const isActive = promptId === activePromptId;
    const isEditing = editingPromptId === promptId;

    return (
      <Draggable key={promptId} draggableId={String(promptId)} index={index}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <Box
              sx={{
                mb: 1.25,
                p: 2,
                borderRadius: '10px',
                border: isActive ? '1px solid rgba(19,148,127,0.45)' : '1px solid #E6EDF2',
                boxShadow: snapshot.isDragging
                  ? '0 16px 32px -12px rgba(11,46,67,0.25)'
                  : 'none',
                background: isActive ? 'rgba(19,148,127,0.05)' : '#ffffff',
                transition: 'border-color 140ms ease, background-color 140ms ease',
                '&:hover': {
                  borderColor: isActive ? 'rgba(19,148,127,0.55)' : '#C3D3DD',
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                <Stack
                  spacing={1.25}
                  sx={{ flex: 1, minWidth: 0, cursor: isEditing ? 'default' : 'pointer' }}
                  onClick={isEditing ? undefined : () => onSelect(promptId, prompt.text)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    {...(isEditing ? {} : provided.dragHandleProps)}
                  >
                    {isActive ? (
                      <TaskAltOutlinedIcon sx={{ color: '#13947F', fontSize: 20 }} />
                    ) : (
                      <RadioButtonUncheckedOutlinedIcon sx={{ color: '#8CA3B2', fontSize: 20 }} />
                    )}
                    <Typography
                      sx={{
                        color: isActive ? '#0C6E5E' : '#7A8FA0',
                        fontWeight: 800,
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {isActive ? 'Active review rule' : 'Available review rule'}
                    </Typography>
                  </Stack>

                  {isEditing ? (
                    <form onSubmit={(event) => onEditSave(promptId, prompt.order, event)}>
                      <Stack spacing={1.5}>
                        <TextField
                          value={editingText}
                          onChange={(event) => onEditChange(event.target.value)}
                          autoFocus
                          fullWidth
                          multiline
                          minRows={3}
                          label="Edit review rule"
                        />
                        <Stack direction="row" spacing={1}>
                          <Button type="submit" size="small" variant="contained" startIcon={<SaveOutlinedIcon />}>
                            Save Rule
                          </Button>
                          <Button type="button" size="small" variant="text" onClick={onEditCancel}>
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    </form>
                  ) : (
                    <Typography sx={{ color: '#244657', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontSize: '0.94rem' }}>
                      {prompt.text}
                    </Typography>
                  )}
                </Stack>

                <Stack direction="row" spacing={0.25} sx={{ alignSelf: 'flex-start' }}>
                  <IconButton size="small" onClick={() => onEditStart(prompt)} aria-label="Edit prompt">
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => onDelete(promptId)} aria-label="Delete prompt">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          </div>
        )}
      </Draggable>
    );
  });
}

export default PromptList;
